/**
 * Pure detection logic for the Usage page (Phase 4). No React, no I/O.
 *
 * Time binning uses the browser's local timezone so "3 AM" matches what
 * the admin would intuit. Day buckets are local-calendar days, not UTC.
 */

import type { UsageEvent } from '@heybesafe/shared';

// ─── Time helpers ─────────────────────────────────────────────────────────

/** Hour-of-day (0-23) in the browser's local timezone. */
export function hourOfDayLocal(iso: string): number {
  return new Date(iso).getHours();
}

/** YYYY-MM-DD string in the browser's local timezone. */
export function dayBucketLocal(iso: string): string {
  const d = new Date(iso);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// ─── Types (used by later tasks) ──────────────────────────────────────────

export type AnomalyFlag =
  | { kind: 'off-hours' }
  | { kind: 'burst'; size: number; durationSec: number }
  | { kind: 'first-item' }
  | { kind: 'new-platform'; platform: string };

export interface AnomalyDetection {
  event: UsageEvent;
  plaintext: { itemName: string; siteUrl: string } | null;
  flags: AnomalyFlag[];
  headline: string;
  subline: string;
}

export interface MemberSummary {
  userId: string;
  email: string;
  daily: number[];              // length 14, oldest → newest
  weekEvents: number;
  lastEventAt: string | null;
  flaggedDays: boolean[];       // length 14
}

export interface AnalysisResult {
  anomalies: AnomalyDetection[];                          // newest-first
  perMember: MemberSummary[];
  flagsByEventId: Map<string, AnomalyFlag[]>;
}

// ─── Detectors ────────────────────────────────────────────────────────────

/**
 * Off-hours detection.
 * Bucket the user's 30-day history by hour-of-day (local). Compute mean
 * bucket count. Hours where the bucket is < 5% of the mean form the
 * "quiet set." If the judged event lands in a quiet hour, flag it.
 *
 * Baseline guard: if the user has < 20 events of history, suppress —
 * not enough data to call any hour quiet.
 */
export function detectOffHours(event: UsageEvent, sameUserHistory: UsageEvent[]): AnomalyFlag | null {
  if (sameUserHistory.length < 20) return null;

  const buckets = new Array<number>(24).fill(0);
  for (const e of sameUserHistory) {
    buckets[hourOfDayLocal(e.occurredAt)] += 1;
  }
  const mean = sameUserHistory.length / 24;
  const quietThreshold = mean * 0.05;
  const hour = hourOfDayLocal(event.occurredAt);
  if (buckets[hour] < quietThreshold) {
    return { kind: 'off-hours' };
  }
  return null;
}

const BURST_WINDOW_MS = 5 * 60 * 1000;
const BURST_THRESHOLD = 4;

/**
 * Burst detection.
 * Count same-userId events whose timestamp falls in a 5-minute window
 * ending at this event. If count >= 4, flag — every event in the burst
 * gets flagged individually (the heads-up panel will collapse them).
 */
export function detectBurst(event: UsageEvent, allEvents: UsageEvent[]): AnomalyFlag | null {
  const tEnd = new Date(event.occurredAt).getTime();
  const tStart = tEnd - BURST_WINDOW_MS;
  const inWindow = allEvents.filter((e) => {
    if (e.userId !== event.userId) return false;
    const t = new Date(e.occurredAt).getTime();
    return t >= tStart && t <= tEnd;
  });
  if (inWindow.length < BURST_THRESHOLD) return null;

  const tMin = Math.min(...inWindow.map((e) => new Date(e.occurredAt).getTime()));
  const tMax = Math.max(...inWindow.map((e) => new Date(e.occurredAt).getTime()));
  return {
    kind: 'burst',
    size: inWindow.length,
    durationSec: Math.round((tMax - tMin) / 1000),
  };
}

const FIRST_ITEM_BASELINE_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * First-ever item detection.
 * Flag if this user has never accessed this itemId before (in history).
 * Baseline guard: suppress if user's history spans < 7 days — fresh
 * accounts would flag every event.
 */
export function detectFirstEverItem(event: UsageEvent, sameUserHistory: UsageEvent[]): AnomalyFlag | null {
  if (sameUserHistory.length === 0) return null;
  const eventTime = new Date(event.occurredAt).getTime();
  const earliest = Math.min(...sameUserHistory.map((e) => new Date(e.occurredAt).getTime()));
  if (eventTime - earliest < FIRST_ITEM_BASELINE_MS) return null;

  const seenItem = sameUserHistory.some((e) => e.itemId === event.itemId);
  return seenItem ? null : { kind: 'first-item' };
}

/**
 * New-platform detection.
 * Flag if the event's platform is a value never seen in same-user history.
 * Events with undefined platform are excluded (don't trigger, don't
 * contribute to the seen set).
 */
export function detectNewPlatform(event: UsageEvent, sameUserHistory: UsageEvent[]): AnomalyFlag | null {
  if (!event.platform) return null;
  const seen = new Set<string>();
  for (const e of sameUserHistory) {
    if (e.platform) seen.add(e.platform);
  }
  return seen.has(event.platform)
    ? null
    : { kind: 'new-platform', platform: event.platform };
}

// ─── Orchestrator ─────────────────────────────────────────────────────────

/**
 * Orchestrator. Given a flat array of events (from a 30-day fetch) and a
 * plaintext map keyed by event.id (null for failed decrypts), run all four
 * detection rules per event, build the per-member summary, and compose
 * heads-up AnomalyDetection objects with collapsed bursts.
 *
 * `now` is parameterised for testability; defaults to wall-clock.
 */
export function analyzeEvents(
  events: UsageEvent[],
  plaintexts: Map<string, { itemName: string; siteUrl: string } | null>,
  now: Date = new Date(),
): AnalysisResult {
  const byUser = new Map<string, UsageEvent[]>();
  for (const e of events) {
    if (!byUser.has(e.userId)) byUser.set(e.userId, []);
    byUser.get(e.userId)!.push(e);
  }

  const flagsByEventId = new Map<string, AnomalyFlag[]>();

  for (const event of events) {
    const history = byUser.get(event.userId) ?? [];
    const historyExcludingSelf = history.filter((e) => e.id !== event.id);
    const flags: AnomalyFlag[] = [];

    const off = detectOffHours(event, historyExcludingSelf);
    if (off) flags.push(off);

    const burst = detectBurst(event, events);
    if (burst) flags.push(burst);

    const first = detectFirstEverItem(event, historyExcludingSelf);
    if (first) flags.push(first);

    const platform = detectNewPlatform(event, historyExcludingSelf);
    if (platform) flags.push(platform);

    if (flags.length > 0) flagsByEventId.set(event.id, flags);
  }

  // Collapse bursts: for each burst, pick the LAST event (the one that
  // triggered the window) as the representative; suppress heads-up cards
  // for earlier members of the same burst.
  const burstRepresentatives = new Set<string>();
  const seenBurstSignatures = new Set<string>();
  for (const event of events.slice().sort((a, b) => a.occurredAt.localeCompare(b.occurredAt))) {
    const flags = flagsByEventId.get(event.id);
    if (!flags) continue;
    const burst = flags.find((f) => f.kind === 'burst');
    if (!burst) continue;
    const sig = `${event.userId}@${event.occurredAt.slice(0, 16)}`;
    if (seenBurstSignatures.has(sig)) continue;
    seenBurstSignatures.add(sig);
    burstRepresentatives.add(event.id);
  }

  // Build heads-up cards
  const anomalies: AnomalyDetection[] = [];
  for (const event of events) {
    const flags = flagsByEventId.get(event.id);
    if (!flags) continue;
    const isBurst = flags.some((f) => f.kind === 'burst');
    if (isBurst && !burstRepresentatives.has(event.id)) continue;

    const plaintext = plaintexts.get(event.id) ?? null;
    anomalies.push({
      event,
      plaintext,
      flags,
      headline: composeHeadline(event, flags, plaintext),
      subline: composeSubline(event, flags, plaintext),
    });
  }

  anomalies.sort((a, b) => b.event.occurredAt.localeCompare(a.event.occurredAt));

  // Per-member summary
  const perMember: MemberSummary[] = [];
  const oldestDayBucket = dayBucketLocal(new Date(now.getTime() - 13 * 24 * 60 * 60 * 1000).toISOString());
  for (const [userId, userEvents] of byUser) {
    userEvents.sort((a, b) => b.occurredAt.localeCompare(a.occurredAt));
    const daily = new Array<number>(14).fill(0);
    const flaggedDays = new Array<boolean>(14).fill(false);

    const bucketIndexFor = (iso: string): number => {
      const evDay = dayBucketLocal(iso);
      if (evDay < oldestDayBucket) return -1;
      const evMs = new Date(`${evDay}T00:00:00`).getTime();
      const oldMs = new Date(`${oldestDayBucket}T00:00:00`).getTime();
      const diff = Math.round((evMs - oldMs) / (24 * 60 * 60 * 1000));
      return diff >= 0 && diff < 14 ? diff : -1;
    };

    for (const e of userEvents) {
      const idx = bucketIndexFor(e.occurredAt);
      if (idx >= 0) {
        daily[idx] += 1;
        if (flagsByEventId.has(e.id)) flaggedDays[idx] = true;
      }
    }

    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
    const weekEvents = userEvents.filter((e) => e.occurredAt >= sevenDaysAgo).length;

    perMember.push({
      userId,
      email: userEvents[0].userEmail,
      daily,
      weekEvents,
      lastEventAt: userEvents[0].occurredAt,
      flaggedDays,
    });
  }
  perMember.sort((a, b) => b.weekEvents - a.weekEvents);

  return { anomalies, perMember, flagsByEventId };
}

function composeHeadline(event: UsageEvent, flags: AnomalyFlag[], _plaintext: { itemName: string; siteUrl: string } | null): string {
  const burst = flags.find((f) => f.kind === 'burst') as (AnomalyFlag & { kind: 'burst' }) | undefined;
  if (burst) {
    const verb = event.action === 'copy' || event.action === 'totp_copy' ? 'copied' : 'autofilled';
    return `Burst: ${event.userEmail} ${verb} ${burst.size} passwords in ${burst.durationSec}s`;
  }
  const firstItem = flags.some((f) => f.kind === 'first-item');
  const offHours = flags.some((f) => f.kind === 'off-hours');
  const time = new Date(event.occurredAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const who = event.userEmail;
  if (firstItem && offHours) return `${who} accessed an item for the first time at ${time}`;
  if (firstItem) return `${who} accessed an item for the first time`;
  if (offHours) return `${who} accessed a sensitive item at ${time}`;
  const newPlatform = flags.find((f) => f.kind === 'new-platform') as (AnomalyFlag & { kind: 'new-platform' }) | undefined;
  if (newPlatform) return `${who} signed in from a new ${newPlatform.platform} client`;
  return `${who} access flagged`;
}

function composeSubline(event: UsageEvent, flags: AnomalyFlag[], plaintext: { itemName: string; siteUrl: string } | null): string {
  const parts: string[] = [];
  if (plaintext?.itemName) parts.push(plaintext.itemName);
  for (const f of flags) {
    if (f.kind === 'off-hours') parts.push('off-hours');
    if (f.kind === 'first-item') parts.push('first-ever access');
    if (f.kind === 'new-platform') parts.push(`new ${f.platform}`);
  }
  parts.push(relativeTime(event.occurredAt));
  return parts.join(' · ');
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60_000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (minutes > 0) return `${minutes}m ago`;
  return 'just now';
}
